
import { useCallback, useMemo } from 'react';
import { logger } from '@/utils/production-logger';
import { TopicType } from '@/types/assistant';
import { MessageType } from '@/types/assistant';

// Import existing hooks
import { useErrorHandlingConsolidation } from '@/hooks/useErrorHandlingConsolidation';
import { useStateCoordination } from '@/hooks/useStateCoordination';
import { useMessagePerformanceTracker } from '@/hooks/useMessagePerformanceTracker';

// Import constants
import { DEFAULT_HOOK_CONFIGS } from '@/constants/ai-assistant-context-constants';

interface UseAiAssistantCoreProps {
  userId: string | null;
  currentSessionId: string | null;
  addFallbackMessage: (message: MessageType) => void;
  removeMessage: (messageId: string) => void;
}

interface UseAiAssistantCoreReturn {
  // Error handling
  consolidatedErrorHandlers: ReturnType<typeof useErrorHandlingConsolidation>;
  
  // State coordination
  stateCoordination: ReturnType<typeof useStateCoordination>;
  
  // Performance tracking
  startMessagePerformance: ReturnType<typeof useMessagePerformanceTracker>['startMessagePerformance'];
  finishMessagePerformance: ReturnType<typeof useMessagePerformanceTracker>['finishMessagePerformance'];
}

/**
 * Core hook that consolidates fundamental AI Assistant functionality
 */
export const useAiAssistantCore = ({
  userId,
  currentSessionId,
  addFallbackMessage,
  removeMessage
}: UseAiAssistantCoreProps): UseAiAssistantCoreReturn => {
  
  // ==================== ERROR HANDLING - TOP LEVEL ====================
  const consolidatedErrorHandlers = useErrorHandlingConsolidation(
    {
      currentSessionId,
      userId,
      componentName: 'AiAssistantProvider'
    },
    addFallbackMessage,
    removeMessage
  );

  // ==================== STATE COORDINATION - FIXED: Moved to top level ====================
  const stateCoordination = useStateCoordination({
    ...DEFAULT_HOOK_CONFIGS.stateCoordination,
    onError: consolidatedErrorHandlers.createCentralizedErrorCallback('state_coordination'),
    onPerformanceIssue: consolidatedErrorHandlers.createPerformanceErrorCallback('state_coordination')
  });

  // ==================== PERFORMANCE TRACKING - TOP LEVEL ====================
  const { startMessagePerformance, finishMessagePerformance } = useMessagePerformanceTracker({
    userId
  });

  // ==================== MEMOIZED RETURN VALUE ONLY ====================
  return useMemo(() => ({
    consolidatedErrorHandlers,
    stateCoordination,
    startMessagePerformance,
    finishMessagePerformance
  }), [consolidatedErrorHandlers, stateCoordination, startMessagePerformance, finishMessagePerformance]);
};
